/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365, { getAsset, getUrlSync } from '@msdyn365-commerce/core';
import { AddToWishlistComponent, IWishlistActionErrorResult } from '@msdyn365-commerce/components';
import { format, ItemAvailability, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { IStoreInfo } from '@msdyn365-commerce-modules/bopis-utilities';
import {
    getConfigureErrors, IBuyboxCallbacks,
    IBuyboxCommonResources,
    IBuyboxData,
    IBuyboxFindInStoreViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps, IBuyboxShopSimilarLookViewProps,
    IBuyboxState
    //IBuyboxViewProps
} from '@msdyn365-commerce-modules/buybox';
import {
    ArrayExtensions,
    DeliveryMode,
    getFullAvailableInventoryNearby,
    GetFullAvailableInventoryNearbyInput,
    IFullOrgUnitAvailability
} from '@msdyn365-commerce-modules/retail-actions';
import { INodeProps, Module, Node, UncontrolledTooltip } from '@msdyn365-commerce-modules/utilities';
import { reaction, toJS } from 'mobx';
import * as React from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';

import { IDlvryBuyboxProps as IBuyboxExtentionProps } from './dlvry-buybox.props.autogenerated';
import { renderAddToOrderTemplateButton } from '../../themes/dlvry-adventureworks/views/components/buybox-add-to-order-template';
import { IBuyboxViewProps } from './dlvry-buybox';
import { GetCookie, SetCookie } from '../../shared-utils/cookie-utilities';
import { getAgreementBadgeForProduct, getCustomerAgreements } from '../../shared-utils/agreement-product-utils';
import { hasAccessToBuy } from '../../shared-utils/attributes';
import { useBrandSchemes } from './hooks/use-brand-schemes';
import DiscountBadge from '../../components/discount-badge/discount-badge';
import { getDiscountBadge } from '../../shared-utils/badge-utils';
import QuantityInput from '../../components/common/quantity-input';
import { getConversionText, getNonBreakoffUOM } from '../../shared-utils/unit-conversion-utils';
import UnitConversionDetails from '../../components/UnitConversionDetails';
import CartCount from '../../components/cart-count';
import ReplacementProducts from './components/replacement-products';
import { LifecycleStatus } from '../../shared-utils/lifecycle-status';
import { getShowAlternativeProductsType, isAvailableToBuy } from '../../shared-utils/common-utils';
import { getTemperatureZoneIcon } from '../../shared-utils/attributes/get-temperature-zone';
import { IProductAgreement } from '../../shared-utils/types';
import { getAgreementIconSource } from '../../shared-utils/agreement-icon-utils';
import { AgreementMark } from '../../components/icons';

export interface IBrandScheme {
    id: string;
    name?: string;
    imageUrl?: string;
}

/**
 * BuyBoxConstants enum.
 */
export enum BuyBoxConstants {
    zero = 0
}

/**
 * Render key in price function.
 * @param keyInPrice - KeyInPrice.
 * @returns -- Returns.
 */
const renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

/**
 * RenderFindInStore.
 * @param findInStore - FindInStore.
 * @returns -- Returns.
 */
const renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

/**
 * RenderAddToOrderTemplate.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToOrderTemplate = (
    props: any,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks
): JSX.Element | null => {
    const product = props.data.product.result;

    if (!props.addToOrderTemplate || !product) {
        return null;
    }

    const orderTemplateButton = renderAddToOrderTemplateButton(props, state, callbacks, product);

    return (
        <Node {...props.addToOrderTemplate.ContainerProps}>
            {props.addToOrderTemplate.errorBlock}
            {orderTemplateButton}
        </Node>
    );
};

/**
 * Add to wishlist failed function.
 * @param callbacks -Buybox callbacks.
 * @param resources -Buybox resources.
 * @param product -Simple product.
 * @returns Update error state.
 */
const onAddToWishlistFailed = (callbacks: IBuyboxCallbacks, resources: IBuyboxCommonResources, product: SimpleProduct | undefined) => (
    result: IWishlistActionErrorResult
) => {
    callbacks.updateErrorState({
        errorHost: 'WISHLIST',
        configureErrors:
            result.status === 'MISSINGDIMENSION' ? getConfigureErrors(result.missingDimensions, resources, product?.IsGiftCard) : {}
    });
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @param product - Product data.
 * @returns -- Returns.
 */
const renderAddtoWishlistButton = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks,
    product: SimpleProduct
): React.ReactNode => {
    const { context, resources } = props;
    const wishlists = props.data.wishlists.result;
    const isShowWishlitButtonText = true;
    return (
        <AddToWishlistComponent
            className='msc-add-to-cart-extra-actions'
            addToWishlistButtonText={resources.addToWishlistButtonText}
            removeFromWishlistButtonText={resources.removeFromWishlistButtonText}
            addToWishlistMessage={resources.addToWishlistMessage}
            removedFromWishlistMessage={resources.removedFromWishlistMessage}
            addItemToWishlistError={resources.addItemToWishlistError}
            removeItemFromWishlistError={resources.removeItemFromWishlistError}
            nameOfWishlist={resources.nameOfWishlist}
            data={{ product, wishlists }}
            context={context}
            ariaRole='button'
            id={props.id}
            typeName={props.typeName}
            onError={onAddToWishlistFailed(callbacks, resources, product)}
            getSelectedProduct={state.selectedProduct}
            showButtonText={isShowWishlitButtonText}
            showButtonTooltip={false}
        />
    );
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox viewprops.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToWishlist = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks
): JSX.Element | null => {
    const product = props.data.product.result;

    if (!props.addToWishlist || !product) {
        return null;
    }

    if (!props.config.enableWishlist) {
        return null;
    }

    const wishlistButton = renderAddtoWishlistButton(props, state, callbacks, product);
    return (
        <Node {...props.addToWishlist.ContainerProps}>
            {props.addToWishlist.errorBlock}
            {wishlistButton}
        </Node>
    );
};

/**
 * RRenderShopSimilarItem.
 * @param shopSimilarItem - ShopSimilarItem.
 * @returns -- Returns.
 */
// @ts-ignore
const renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

/**
 * RenderSocialShare.
 * @param socialShare - Social share react node array.
 * @returns -- Returns.
 */
const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

/**
 * RenderConfigureDropdown.
 * @param dropdown - Buybox product configure dropdown view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {select}
            {errors}
        </Node>
    );
};

/**
 * RenderConfigure.
 * @param configure - Buybox product configure view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(renderConfigureDropdown)}</Node>;
};

/**
 * BuyboxView.
 * @param props - Props.
 * @returns -- Returns.
 */
const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>> = props => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const {
        context,
        ModuleProps,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        MediaGalleryContainerProps,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        productComparisonButton,
        configure,
        description,
        findInStore,
        price,
        totalPrice,
        deposit,
        pricePer,
        pcsPrice,
        kilogramPrice,
        literPrice,
        additionalInformation,
        agentImporter,
        title,
        region,
        inventoryLabel,
        keyInPrice,
        callbacks,
        state,
        resources,
        app: { config: appConfig },
        data: {
            product: { result: product },
            productSpecificationData: { result: productSpecificationData }
        }
    } = props;

    // Track recently viewed items
    if (props.config.trackRecentlyViewedItems) {
        _updateRecentlyViewedItemsCookie(props);
    }

    const preferredStoreInfo: IStoreInfo | undefined = props.data.storeSelectorStateManager.result?.preferredStore;
    const preferredStoreId = preferredStoreInfo?.StoreId ? preferredStoreInfo.StoreId : '';
    const preferredStoreName = preferredStoreInfo?.StoreName ? preferredStoreInfo.StoreName : '';

    const [availability, setAvailability] = React.useState<string>('');
    const [isStoreChange, setIsStoreChange] = React.useState<boolean>(false);
    const [showUnitConversionDetails, setShowUnitConversionDetails] = React.useState<boolean>(false);
    const orderTemplateIndicatorRef = React.useRef<HTMLDivElement>(null);
    const [brandSchemes] = useBrandSchemes(props.context, productSpecificationData, true);

    // True adds fade-in class to modal, false removes it
    const [fadeInReplacementModal, setFadeInReplacementModal] = React.useState<boolean>(false);
    // True sets display of modal to flex, false sets it to none
    const [showReplacementModal, setShowReplacementModal] = React.useState<boolean>(false);

    const toggleReplacementModal = (show: boolean) => {
        if (show) {
            setShowReplacementModal(true);
            setTimeout(() => {
                setFadeInReplacementModal(true);
            }, 10);
        } else {
            setFadeInReplacementModal(false);
            setTimeout(() => {
                setShowReplacementModal(false);
            }, 200);
        }
    };

    React.useEffect(() => {
        if (showReplacementModal) {
            document.body.classList.add('ms-buybox__replacement-modal-open');
        } else {
            document.body.classList.remove('ms-buybox__replacement-modal-open');
        }
    }, [showReplacementModal]);

    /**
     * GetStockInventoryLabel.
     * @param availabilityWithHours - Stock availability with hours.
     * @returns -- Returns.
     */
    const getStockInventoryLabel = (availabilityWithHours: IFullOrgUnitAvailability | undefined): string | undefined => {
        if (availabilityWithHours && ArrayExtensions.hasElements(availabilityWithHours.ProductInventoryInformation)) {
            for (const productInventoryInformation of availabilityWithHours.ProductInventoryInformation) {
                if (
                    availabilityWithHours.OrgUnitAvailability?.OrgUnitLocation?.InventoryLocationId ===
                    productInventoryInformation.InventLocationId
                ) {
                    return productInventoryInformation.StockLevelLabel;
                }
            }
        }

        return undefined;
    };

    const getInventory = React.useCallback(async () => {
        const input = new GetFullAvailableInventoryNearbyInput(
            product?.RecordId,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            true
        );

        /**
         * IsProductInStock.
         * @param itemAvailabilities - Item availability array.
         * @returns -- Returns.
         */
        const isProductInStock = (itemAvailabilities: ItemAvailability[] | undefined): boolean => {
            if (!appConfig.enableStockCheck) {
                return true;
            }

            if (ArrayExtensions.hasElements(itemAvailabilities)) {
                return itemAvailabilities[BuyBoxConstants.zero].AvailableQuantity! > BuyBoxConstants.zero;
            }

            return false;
        };

        await getFullAvailableInventoryNearby(input, props.context.actionContext)
            .catch((error: Error) => {
                props.telemetry.error(error.message);
            })
            .then(response => {
                if (response && ArrayExtensions.hasElements(response)) {
                    const isPreferredStore = (value: IFullOrgUnitAvailability) =>
                        value.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber === preferredStoreId;
                    const filteredFullOrgUnitAvailability = response.filter(isPreferredStore);
                    const preferredStoreAvailability = ArrayExtensions.hasElements(filteredFullOrgUnitAvailability)
                        ? filteredFullOrgUnitAvailability[BuyBoxConstants.zero]
                        : undefined;
                    const stockStatusLabel: string | undefined = getStockInventoryLabel(preferredStoreAvailability);
                    const isInStock = isProductInStock(preferredStoreAvailability?.OrgUnitAvailability?.ItemAvailabilities);
                    const resourceLabel = isInStock ? props.resources.inStockText : props.resources.outOfStockText;
                    const stockText: string | undefined = stockStatusLabel ? stockStatusLabel : resourceLabel;
                    const preferredStoreAvailabilityText = `${format(
                        props.resources.availabilityAtPreferredStoreText,
                        preferredStoreName
                    )} ${stockText}`;
                    setAvailability(preferredStoreAvailabilityText);
                }
            });

        setIsStoreChange(false);
    }, [
        product?.RecordId,
        appConfig.enableStockCheck,
        preferredStoreId,
        preferredStoreName,
        props.context.actionContext,
        props.resources.availabilityAtPreferredStoreText,
        props.resources.inStockText,
        props.resources.outOfStockText,
        props.telemetry
    ]);

    React.useEffect(() => {
        if (props.config.displayStockAvailability) {
            getInventory().catch((error: Error) => {
                props.telemetry.error(error.message);
            });
        }
    }, [getInventory, isStoreChange, props.telemetry, props.config.displayStockAvailability]);

    // Mirror button text to title of Microsoft buttons
    React.useEffect(() => {
        const setTitleToTextContent = (element: HTMLElement | null) => {
            if (element) {
                element.title = element?.textContent || '';
            }
        };

        setTitleToTextContent(document.querySelector('.msc-add-to-wishlist.msc-add-to-cart-extra-actions'));
        setTitleToTextContent(document.querySelector('.msc-add-to-cart-extra-actions.msc-add-to-order-template-icon'));
    }, [props.data.wishlists.result, product, props.data.orderTemplates.result]);

    reaction(
        () => props.data.storeSelectorStateManager.result?.selectedStoreLocationId,
        () => {
            setIsStoreChange(true);
        }
    );

    /**
     * RenderAvailabiltyAtPreferredStore.
     * @returns -- Returns.
     */
    const renderAvailabilityAtPreferredStore = (): JSX.Element | undefined => {
        const hasProductDimensions = ArrayExtensions.hasElements(product?.Dimensions);
        if (hasProductDimensions && product?.MasterProductId === undefined) {
            return undefined;
        }
        const containerProps: INodeProps = {
            className: 'ms-buybox__preferred-store-availability',
            tag: 'span'
        };

        return <Node {...containerProps}>{availability}</Node>;
    };

    const isAuthenticated = props.context.request.user.isAuthenticated;
    const hasAccess = hasAccessToBuy(context, props.data.organizationInformation.result, productSpecificationData);
    const conversionText = getConversionText(
        context,
        product?.DefaultUnitOfMeasure,
        props.data.productUnitOfMeasureConversions.result || [],
        product,
        state.breakoff,
        product
    );
    const unitConversionDetailsEnabled = context.app.config?.enableUnitConversionDetails;

    const replacements = props.data.replacements.result || [];
    const shippingInventory = props.data.productAvailableQuantity.result?.find(shipping => shipping.deliveryType === DeliveryMode.shipping);
    const relationTypes = toJS(replacements).flatMap(replacement => replacement.RelationTypes || []);
    const showAlternativeProductsType = getShowAlternativeProductsType(context, relationTypes, shippingInventory);
    const lifeCycleStatus = product?.ExtensionProperties?.find(x => x.Key === 'PRODUCTLIFECYCLESTATEID')?.Value?.StringValue;
    const isOutOfSeason = lifeCycleStatus === LifecycleStatus.OutOfSeason;
    const allowBuying = isAvailableToBuy(context, product, showAlternativeProductsType, shippingInventory) && hasAccess;
    const discountBadge = getDiscountBadge(props.data.productPrice.result, product?.FallbackBadge);
    const temperatureZoneIcon = product?.TemperatureZone ? getTemperatureZoneIcon(context, product.TemperatureZone) : undefined;
    const customerAgreements = getCustomerAgreements(context, props.data.organizationInformation.result);
    // Get customer agreements that apply for this product
    const productAgreements = customerAgreements?.filter(x => product?.Agreements?.find(y => y.AttributeRecordId === x.AttributeRecordId));
    // Get list of all agreement icons that should be shown for this product. Filter out duplicates
    const agreementIcons = productAgreements?.filter(x => x.IconFileName && (x.DisplayType === 'both' || x.DisplayType === 'icon')).reduce((acc, curr) => {
        if (!acc.find(x => x.IconFileName === curr.IconFileName)) {
            acc.push(curr);
        }
        return acc;
    }, [] as IProductAgreement[]);
    // Check if there is any agreements without a specified icon, show a single generic icon if so
    const hasAgreementWithoutIcon = productAgreements?.some(x => !x.IconFileName && (x.DisplayType ? x.DisplayType === 'both' || x.DisplayType === 'icon' : true));
    const agreementBadge = getAgreementBadgeForProduct(product, customerAgreements);

    return (
        <Module {...ModuleProps}>
            <Node className='ms-buybox__product-top'>
                <Node style={{ position: 'relative' }} {...MediaGalleryContainerProps}>
                    {props.mediaGallery}
                    {(discountBadge || agreementBadge?.Badge) && (
                        <div className='ms-buybox__image-badge-wrapper'>
                            {discountBadge && (
                                <DiscountBadge
                                    context={props.context}
                                    badge={discountBadge}
                                    size='large' />
                            )}
                            {agreementBadge?.Badge && (
                                <DiscountBadge
                                    context={props.context}
                                    badge={agreementBadge.Badge}
                                    size='large'
                                    title={agreementBadge.TooltipText} />
                            )}
                        </div>
                    )}
                </Node>
                <Node {...ProductInfoContainerProps}>
                    <div className='media-gallery-actions'>
                        {isAuthenticated && addToWishlist && renderAddToWishlist(props, state, callbacks)}
                        {isAuthenticated && addToOrderTemplate && renderAddToOrderTemplate(props, state, callbacks)}
                        {isAuthenticated && productComparisonButton}
                    </div>
                    <div className='ms-buybox__title-row'>
                        {hasAgreementWithoutIcon && (
                            <span className='ms-buybox__agreement-product-svg' title={resources.agreementProductTooltip || 'Avtalevare'}>
                                <AgreementMark height={26} width={26} />
                            </span>
                        )}
                        {agreementIcons?.map((icon, index) => (
                            <img
                                key={index}
                                className='ms-buybox__agreement-product-icon'
                                src={getAgreementIconSource(icon.IconFileName!, assetPath => getAsset(assetPath, context.request))}
                                title={icon.IconTooltipText || icon.TooltipText}
                            />
                        ))}
                        {temperatureZoneIcon && (
                            <span
                                className={`ms-buybox__temperature-icon ${temperatureZoneIcon}`}
                                title={product?.TemperatureZone} />
                        )}
                        {title}
                    </div>
                    <div className='ms-buybox__under-title-section'>
                        {product?.ItemId && (
                            <div className='ms-buybox__product-item-id'>
                                {product?.ItemId}
                            </div>
                        )}
                        {additionalInformation}
                        {agentImporter}
                        {region}
                        <div
                            className={classnames('ms-buybox__product-conversion', { enabled: unitConversionDetailsEnabled })}
                            style={{ visibility: conversionText ? undefined : 'hidden' }}
                            onClick={() => setShowUnitConversionDetails(!showUnitConversionDetails)}
                        >
                            <span>
                                {conversionText || '.'}
                            </span>
                            <UnitConversionDetails
                                context={context}
                                enabled={!!unitConversionDetailsEnabled}
                                isOpen={showUnitConversionDetails}
                                toggle={() => setShowUnitConversionDetails(!showUnitConversionDetails)}
                                productName={product?.Name}
                                productUOM={product?.DefaultUnitOfMeasure}
                                unitConversions={props.data.productUnitOfMeasureConversions.result || []}
                                simpleProduct={product}
                                breakoff={props.state.breakoff}
                            />
                        </div>
                    </div>
                    {description && <Node className='ms-buybox__description-section'>
                        {description}
                    </Node>}
                    <div className='msc-buybox__buy-section'>
                        {!isAuthenticated && (
                            <div className='msc-buybox__login-message'>
                                {resources.priceNotLoggedInText}
                            </div>
                        )}
                        {isAuthenticated && !allowBuying && isOutOfSeason && (
                            <div className='msc-buybox__out-of-season-message'>
                                {(resources as any).outOfSeasonText || 'Varen er ute av sesong'}
                            </div>
                        )}
                        {isAuthenticated && allowBuying && price && (
                            <Node className='msc-buybox__price-section'>
                                {price}
                                {price && (
                                    <Node className='msc-buybox__price-section__extra'>
                                        {pricePer}
                                        {pcsPrice}
                                        {kilogramPrice}
                                        {literPrice}
                                        {deposit}
                                    </Node>
                                )}
                                {state.firstExpiringDiscount && (
                                    <div className='msc-buybox__price-section__expiring'>
                                        {format(resources.expiringDiscountText, context.cultureFormatter.formatDate(state.firstExpiringDiscount, {
                                            day: '2-digit',
                                            month: 'long',
                                            year: state.firstExpiringDiscount.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
                                        }))}
                                    </div>
                                )}
                            </Node>
                        )}
                        {isAuthenticated && allowBuying && (
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <QuantityInput
                                    context={props.context}
                                    product={product}
                                    extProduct={product}
                                    quantity={state.quantity}
                                    breakoff={state.breakoff}
                                    onQuantityChange={callbacks.updateQuantity}
                                    onBreakoffChange={props.onBreakoffChange}
                                    nonBreakoffUOM={getNonBreakoffUOM(product, props.data.productUnitOfMeasureConversions.result)}
                                    unitConversions={props.data.productUnitOfMeasureConversions.result}
                                />
                            </div>
                        )}
                        {isAuthenticated && allowBuying && totalPrice}
                        {isAuthenticated && hasAccess && (
                            <Node {...addToCart.ContainerProps}>
                                {showAlternativeProductsType && (
                                    <>
                                        {!isOutOfSeason && showAlternativeProductsType === 'Primary' && (
                                            <div className={classnames(`ms-buybox__change-product__information__wrapper`)}>
                                                <div className={`ms-buybox__change-product__information__text`}>
                                                    Varen er sluttsolgt
                                                </div>
                                            </div>
                                        )}
                                        <button
                                            className={classnames('ms-buybox__replacement-product-button', 'secondary')}
                                            onClick={() => toggleReplacementModal(true)}
                                        >
                                            {!isOutOfSeason && showAlternativeProductsType === 'Primary' ? 'Se erstatningsvare' : 'Se alternativer'}
                                        </button>
                                        {MsDyn365.isBrowser && ReactDOM.createPortal(
                                            <div
                                                className={classnames('ms-buybox__replacement-modal', { 'is-open': showReplacementModal, 'fade-in': fadeInReplacementModal })}
                                                onClick={(e) => {
                                                    if (e.target === e.currentTarget) {
                                                        toggleReplacementModal(false);
                                                    }
                                                }}
                                            >
                                                <div className='ms-buybox__replacement-modal__content'>
                                                    <div className='ms-buybox__replacement-modal__header'>
                                                        <h1>Gå til alternativer</h1>
                                                        <button
                                                            className='ms-buybox__replacement-modal__close-button'
                                                            onClick={() => toggleReplacementModal(false)}
                                                            aria-label='Close'
                                                        >
                                                        </button>
                                                    </div>
                                                    <ReplacementProducts
                                                        id={props.id}
                                                        typeName={props.typeName}
                                                        context={context}
                                                        replacements={replacements}
                                                    />
                                                    <div className='ms-buybox__replacement-modal__footer'>
                                                        <button className='ms-buybox__replacement-modal__footer__close-button' onClick={() => toggleReplacementModal(false)}>
                                                            Lukk
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        , document.body)}
                                    </>
                                 )}
                                {showAlternativeProductsType !== 'Primary' && addToCart.button}
                            </Node>
                        )}
                        {isAuthenticated && (
                            <div style={{ display: 'flex', gap: 6, marginTop: 14, justifyContent: 'flex-end' }}>
                                {allowBuying && inventoryLabel}
                                <CartCount
                                    context={context}
                                    cart={props.data.cart.result?.cart}
                                    product={product}
                                    nonBreakoffUOM={getNonBreakoffUOM(product, props.data.productUnitOfMeasureConversions.result)}
                                />
                                {props.data.productLists.result && props.data.productLists.result.length > 0 && (
                                    <div className='msc-buybox__order-template-indicator'>
                                        <div className='msc-buybox__order-template-indicator__content' ref={orderTemplateIndicatorRef}>P</div>
                                        <UncontrolledTooltip
                                            className='msc-buybox__order-template-indicator__tooltip'
                                            placement='bottom-end'
                                            trigger='hover focus'
                                            delay={{ show: 200, hide: 200 }}
                                            target={orderTemplateIndicatorRef}
                                        >
                                            <div className='msc-buybox__order-template-indicator__tooltip-header'>
                                                {/* inOrderTemplateTooltipHeader ||  */'Finnes i plankjøp:'}
                                            </div>
                                            {props.data.productLists.result && props.data.productLists.result.length > 0 && (
                                                <ul>
                                                    {props.data.productLists.result.map((productList) => {
                                                        let url = getUrlSync('ordertemplate', context.actionContext) ?? '';
                                                        const separator = url.includes('?') ? '&' : '?';

                                                        if (productList.ProductListId) {
                                                            url = `${url}${separator}id=${productList.ProductListId}`;
                                                        }

                                                        return (
                                                            <li key={productList.ProductListId}>
                                                                <a href={url}>{productList.Name}</a>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            )}
                                        </UncontrolledTooltip>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className='msc-buybox__brand-schemes'>
                        {brandSchemes && brandSchemes.length > 0 && brandSchemes.map((scheme, index) => (
                            <div key={index} className='msc-buybox__brand-schemes__item'>
                                <div className='msc-buybox__brand-schemes__img'>
                                    {scheme.imageUrl
                                        ? <img src={scheme.imageUrl} alt={scheme.name} title={scheme.name} />
                                        : <div className='missing-icon' title={`Couldn't find full text and image for "${scheme.id}"`}>
                                            <i className='fa-solid fa-square-question' />
                                            {scheme.id}
                                        </div>
                                    }
                                </div>
                            </div>
                        ))}
                        {!brandSchemes || brandSchemes.length === 0 ? (
                            <div className='msc-buybox__brand-schemes__item'>
                                <div className='msc-buybox__brand-schemes__img' />
                            </div>
                        ) : null}
                    </div>
                    {configure && renderConfigure(configure)}
                    {keyInPrice && renderKeyInPrice(keyInPrice)}
                    {isAuthenticated && !hasAccess && <div className='msc-buybox__missing-license'>{resources.missingLicenseText}</div>}
                    {isAuthenticated && props.config.displayStockAvailability && preferredStoreId && renderAvailabilityAtPreferredStore()}
                    {isAuthenticated && findInStore && renderFindInStore(findInStore)}
                    {isAuthenticated && findInStore?.productPickupOptionList ? (
                        <div className='msc-buybox__pickup-options'>{findInStore.productPickupOptionList}</div>
                    ) : null}
                    {_renderSocialShare(props.slots && props.slots.socialShare)}
                </Node>
            </Node>
            {ArrayExtensions.hasElements(props.slots.alternativeProductsCarousel) ? (
                <Node className='msc-buybox__alternative-products-carousel'>
                    {props.slots.alternativeProductsCarousel[0]}
                </Node>
            ) : null}
            {ArrayExtensions.hasElements(props.slots.productSpecification) ? (
                <Node className='msc-buybox__product-specification'>{props.slots.productSpecification[0]}</Node>
            ) : null}
        </Module>
    );
};

/**
 * Keep track of recently viewed items.
 * @param props
 */
const _updateRecentlyViewedItemsCookie = (props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>): void => {
    const cookieName = '_i365___recently_viewed_products';
    const cookieAge: number = 31536000000;
    const cookieValue = GetCookie(props.context.request.cookies, cookieName);
    let productIds = (cookieValue && cookieValue.split(',').map(x => +x)) || [];
    const recordId: number = (props.data.product.result && props.data.product.result.RecordId) || 0;
    const max: number = props.config.maxRecentlyViewedItemsCount || 10;
    if (recordId !== 0 && productIds.indexOf(recordId) === -1) {
        productIds.push(recordId);
    }
    if (productIds.length > max) {
        productIds = productIds.slice(Math.max(productIds.length - max, 0));
    }
    const newCookieValue = String(productIds);
    SetCookie(props.context.request.cookies, newCookieValue, cookieName, cookieAge);
};
export default BuyboxView;
